import {
  Stack,
  Typography,
  Card,
  CardContent,
  Box,
  Skeleton,
  TextField,
  InputAdornment,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useTheme,
  Divider,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { GenericTable } from "@/components/GenericTable/GenericTable";

// Função para calcular a largura ideal do eixo Y baseada no tamanho dos labels
const calculateYAxisWidth = (data: { label: string; value: number }[]) => {
  if (!data || data.length === 0) return 150;
  const maxLabelLength = Math.max(...data.map((item) => item.label.length));
  const calculatedWidth = Math.max(150, maxLabelLength * 7 + 20);
  return Math.min(calculatedWidth, 400);
};
import SearchIcon from "@mui/icons-material/Search";
import { GetQuestionnaireResponse } from "@/services/fhir/questionnaireresponse/GetQuestionnaries";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { oids } from "@/configs/Settings";
import { useAdministrativeFilters } from "@/pages/adminInsights/shared/useAdministrativeFilters";
import { useDateRangeFilter } from "@/components/common/DateRangeFilter/useDateRangeFilter";
import { endpoints } from "@/configs/Settings";
import { ViewHeader } from "@/components/schedule/components/ViewHeader";
import { DateFilterType } from "@/utils/interfaces";
import { useAxios } from "@/utils/useAxios";
import { useAuth } from "@/utils/useAuth";
import { HTTPResponseStatus } from "@/utils/enum";
import { NoData } from "@/components/common/NoData";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`problem-tabpanel-${index}`}
      aria-labelledby={`problem-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

interface ProblemData {
  problem: string;
  status: string;
  practitioner: string;
  date: string;
  patientId: string;
  patientName?: string;
}

interface PatientProblemGroup {
  patientId: string;
  patientName: string;
  totalProblems: number;
  problems: ProblemData[];
  statusCounts: {
    melhora: number;
    aberta: number;
    estável: number;
    piora: number;
  };
}

interface ProblemStats {
  status: string;
  count: number;
  percentage: number;
}

export function ProblemListAnalysis({
  count,
  data,
}: {
  count?: number;
  data?: any;
}) {
  const theme = useTheme();
  const { organizationId } = useOrganizationContext();
  const [currentTab, setCurrentTab] = useState(0);
  const [search, setSearch] = useState<string>("");
  const [problems, setProblems] = useState<ProblemData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [patientDialogOpen, setPatientDialogOpen] = useState<boolean>(false);
  const [patientProblems, setPatientProblems] = useState<ProblemData[]>([]);
  const [patients, setPatients] = useState<Map<string, fhir5.Patient>>(
    new Map()
  );
  const [dialogSearch, setDialogSearch] = useState<string>("");
  const [problemSearch, setProblemSearch] = useState<string>("");

  // Filtros de data
  const {
    selectedDate,
    dateFilterType,
    dateRange,
    handleDateChange,
    handleFilterTypeChange,
  } = useDateRangeFilter({ defaultFilterType: "month" });

  // Converte dateFilterType para DateFilterType (removendo "year" se necessário)
  const filterType: DateFilterType =
    dateFilterType === "year" ? "month" : (dateFilterType as DateFilterType);

  // Wrapper para handleFilterTypeChange que converte "year" para "month"
  const handleFilterTypeChangeWrapper = (type: DateFilterType) => {
    handleFilterTypeChange(type as any);
  };

  // Filtros administrativos
  const {
    selectedLocation,
    selectedPractitioner,
    handleLocationChange,
    handlePractitionerChange,
    location,
    locationLoading,
    practitionerRole,
    practitioner,
    practitionerRoleLoading,
  } = useAdministrativeFilters({
    onChange: (filters) => {
      console.log("Filtros de problemas atualizados:", filters);
    },
  });

  const author = `${oids.cnpj}-${organizationId}`;
  const practitionerFilter = selectedPractitioner || "all";

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  // Função para resetar todos os filtros
  const handleReset = () => {
    handleLocationChange("");
    handlePractitionerChange("");
  };

  // Função para obter o nome do profissional a partir da referência
  const getPractitionerName = useCallback(
    (practitionerReference: string) => {
      if (!practitionerReference) return "Não informado";

      // Extrai o ID do profissional da referência
      const practId = practitionerReference.split("/")[1];

      // Busca o recurso Practitioner correspondente
      const pract = practitioner?.find((p: any) => p.resource?.id === practId);

      // Retorna o nome formatado
      const displayName =
        pract?.resource?.name?.[0]?.text ||
        `${pract?.resource?.name?.[0]?.given?.join(" ") || ""} ${
          pract?.resource?.name?.[0]?.family || ""
        }`.trim();

      return displayName || practId;
    },
    [practitioner]
  );

  // Função para buscar dados dos pacientes
  const fetchPatients = async (patientIds: string[]) => {
    try {
      const patientMap = new Map<string, fhir5.Patient>();

      for (const patientRef of patientIds) {
        const patientId = patientRef.split("/")[1];
        if (!patientId || patientMap.has(patientId)) continue;

        const url = `${endpoints.MPIFHIR}/Patient/${patientId}`;
        const headers = {
          Authorization: await useAuth.getUserToken(),
          "Content-Type": "application/fhir+json",
          Accept: "application/fhir+json",
        };

        try {
          const response = await useAxios.get(url, { headers, timeout: 20000 });
          if (response.status === HTTPResponseStatus.Success && response.data) {
            patientMap.set(patientId, response.data);
          }
        } catch (err) {
          console.error(`Erro ao buscar paciente ${patientId}:`, err);
        }
      }

      setPatients(patientMap);
    } catch (error) {
      console.error("Erro ao buscar pacientes:", error);
    }
  };

  // Buscar dados da lista de problemas
  const fetchProblemsData = useCallback(async () => {
    if (!author) return;

    setLoading(true);
    try {
      // Buscar QuestionnaireResponse para pegar lista de problemas
      const response = await GetQuestionnaireResponse({
        author,
        practitioner: practitionerFilter,
        hasProtocols: false,
        questionnaire: "Questionnaire/clinicalRegister",
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
      });

      const entries: any[] = response?.entry || [];

      // Map para agrupar problemas por paciente + nome do problema
      const problemMap = new Map<
        string,
        {
          problem: string;
          status: string;
          practitioner: string;
          date: string;
          patientId: string;
          authored: Date;
        }
      >();

      for (const entry of entries) {
        const resource = entry?.resource;
        if (!resource || resource.resourceType !== "QuestionnaireResponse")
          continue;

        // Buscar o item de Lista de Problemas (linkId: "f075e9cc-c1c4-44ac-db18-035cbc5453d4")
        const problemsItem = resource.item?.find(
          (item: any) => item.linkId === "f075e9cc-c1c4-44ac-db18-035cbc5453d4"
        );

        if (!problemsItem?.item) continue;

        // ID do paciente
        const patientId = resource.subject?.reference || "unknown";

        // Nome do profissional
        const practitionerName = getPractitionerName(
          resource.author?.reference || ""
        );

        // Data do atendimento
        const authoredDate = resource.authored
          ? new Date(resource.authored)
          : new Date(0);
        const date = authoredDate.toLocaleDateString();

        // Processar cada problema (linkId: "8e2c9b72-bcee-4c0f-ecb2-8e4fc2866cd4")
        const seenInThisQuestionnaire = new Set<string>();

        for (const problemGroup of problemsItem.item) {
          if (
            !problemGroup.item ||
            problemGroup.linkId !== "8e2c9b72-bcee-4c0f-ecb2-8e4fc2866cd4"
          )
            continue;

          // Buscar o problema (linkId: "4f96c740-f739-4758-8324-b5fd7893fcbd")
          const problemItem = problemGroup.item.find(
            (item: any) =>
              item.linkId === "4f96c740-f739-4758-8324-b5fd7893fcbd"
          );

          const problemText = problemItem?.answer?.[0]?.valueString;
          if (!problemText) continue; // Pula se não tiver texto de problema

          // Buscar o status do problema (linkId: "2972b2f4-a634-4277-8200-96ce9111dc3d")
          const statusItem = problemGroup.item.find(
            (item: any) =>
              item.linkId === "2972b2f4-a634-4277-8200-96ce9111dc3d"
          );

          // Se não tiver status, está em aberta
          let status = "aberta";

          if (statusItem?.answer?.[0]?.valueCoding) {
            const statusDisplay = statusItem.answer[0].valueCoding.display;

            if (statusDisplay === "Melhora") {
              status = "melhora";
            } else if (statusDisplay === "Estável") {
              status = "estável";
            } else if (statusDisplay === "Piora") {
              status = "piora";
            }
          }

          // Chave única para evitar duplicatas no mesmo questionário
          // - Com resposta (status != "aberta"): problema + status
          // - Sem resposta (status == "aberta"): apenas problema
          const uniqueKey =
            status === "aberta" ? problemText : `${problemText}-${status}`;

          // Evita duplicatas no mesmo questionário usando a mesma lógica de chave
          if (seenInThisQuestionnaire.has(uniqueKey)) continue;
          seenInThisQuestionnaire.add(uniqueKey);

          // Chave única global:
          // - Com resposta (status != "aberta"): paciente + problema + status
          // - Sem resposta (status == "aberta"): paciente + problema
          const key =
            status === "aberta"
              ? `${patientId}-${problemText}`
              : `${patientId}-${problemText}-${status}`;

          // Se já existe, mantém o mais recente
          const existing = problemMap.get(key);
          if (!existing || authoredDate > existing.authored) {
            problemMap.set(key, {
              problem: problemText,
              status,
              practitioner: practitionerName,
              date,
              patientId,
              authored: authoredDate,
            });
          }
        }
      }

      // Aplicar lógica de paridade (igual ao formulário original)
      // Regras:
      // - Agrupar por paciente + texto do problema (não por status)
      // - PAR: mostra apenas 1 registro (com status ou mais recente)
      // - ÍMPAR: mostra até 2 registros (um com status + um sem status)
      // - EXCEÇÃO: Sempre manter itens do QuestionnaireResponse mais recente do paciente
      const patientLatestAuthored = new Map<string, number>();
      for (const v of problemMap.values()) {
        const t = v.authored.getTime();
        const prev = patientLatestAuthored.get(v.patientId) || 0;
        if (t > prev) patientLatestAuthored.set(v.patientId, t);
      }

      // Agrupar por paciente + problema (não por status)
      const groups = new Map<
        string,
        Array<{
          problem: string;
          status: string;
          practitioner: string;
          date: string;
          patientId: string;
          authored: Date;
        }>
      >();

      for (const value of problemMap.values()) {
        const key = `${value.patientId}|${value.problem}`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(value);
      }

      const parityProblems: ProblemData[] = [];
      for (const [, items] of groups.entries()) {
        if (items.length === 1) {
          parityProblems.push({
            problem: items[0].problem,
            status: items[0].status,
            practitioner: items[0].practitioner,
            date: items[0].date,
            patientId: items[0].patientId,
          });
          continue;
        }

        // Ordena por data (mais recente primeiro)
        const sorted = items.sort(
          (a, b) => b.authored.getTime() - a.authored.getTime()
        );
        const isPair = items.length % 2 === 0;
        const withStatus = sorted.filter(
          (it) => it.status && it.status !== "aberta"
        );
        const withoutStatus = sorted.filter(
          (it) => !it.status || it.status === "aberta"
        );

        const latestAuthoredTs = patientLatestAuthored.get(sorted[0].patientId);
        const latestBelongsToCurrentQR =
          latestAuthoredTs !== undefined &&
          sorted[0].authored.getTime() === latestAuthoredTs;

        // Se pertence ao QR mais recente, sempre manter TODAS as entradas
        if (latestBelongsToCurrentQR) {
          // Para QR mais recente, sempre manter todas as entradas (ignorar paridade)
          for (const item of sorted) {
            parityProblems.push({
              problem: item.problem,
              status: item.status,
              practitioner: item.practitioner,
              date: item.date,
              patientId: item.patientId,
            });
          }
        } else {
          // Lógica normal de paridade
          if (isPair) {
            // PAR: apenas 1 registro
            if (withStatus.length > 0) {
              parityProblems.push({
                problem: withStatus[0].problem,
                status: withStatus[0].status,
                practitioner: withStatus[0].practitioner,
                date: withStatus[0].date,
                patientId: withStatus[0].patientId,
              });
            } else {
              parityProblems.push({
                problem: sorted[0].problem,
                status: sorted[0].status,
                practitioner: sorted[0].practitioner,
                date: sorted[0].date,
                patientId: sorted[0].patientId,
              });
            }
          } else {
            // ÍMPAR: até 2 registros
            if (withStatus.length > 0) {
              parityProblems.push({
                problem: withStatus[0].problem,
                status: withStatus[0].status,
                practitioner: withStatus[0].practitioner,
                date: withStatus[0].date,
                patientId: withStatus[0].patientId,
              });
              if (withoutStatus.length > 0) {
                parityProblems.push({
                  problem: withoutStatus[0].problem,
                  status: withoutStatus[0].status,
                  practitioner: withoutStatus[0].practitioner,
                  date: withoutStatus[0].date,
                  patientId: withoutStatus[0].patientId,
                });
              }
            } else {
              parityProblems.push({
                problem: sorted[0].problem,
                status: sorted[0].status,
                practitioner: sorted[0].practitioner,
                date: sorted[0].date,
                patientId: sorted[0].patientId,
              });
            }
          }
        }
      }

      setProblems(parityProblems);

      // Buscar dados dos pacientes
      const uniquePatientIds = Array.from(
        new Set(parityProblems.map((p) => p.patientId))
      );
      await fetchPatients(uniquePatientIds);
    } catch (error) {
      console.error("Erro ao buscar problemas:", error);
      setProblems([]);
    } finally {
      setLoading(false);
    }
  }, [
    author,
    practitionerFilter,
    dateRange?.startDate,
    dateRange?.endDate,
    getPractitionerName,
  ]);

  useEffect(() => {
    if (author) {
      fetchProblemsData();
    }
  }, [fetchProblemsData, author]);

  // Agrupar problemas por paciente
  const patientGroups = useMemo(() => {
    const groups = new Map<string, PatientProblemGroup>();

    for (const problem of problems) {
      const patientId = problem.patientId.split("/")[1];
      const patient = patients.get(patientId);
      const patientName =
        patient?.name?.[0]?.text ||
        `${patient?.name?.[0]?.given?.join(" ") || ""} ${
          patient?.name?.[0]?.family || ""
        }`.trim() ||
        "Não informado";

      if (!groups.has(patientId)) {
        groups.set(patientId, {
          patientId,
          patientName,
          totalProblems: 0,
          problems: [],
          statusCounts: {
            melhora: 0,
            aberta: 0,
            estável: 0,
            piora: 0,
          },
        });
      }

      const group = groups.get(patientId)!;
      group.totalProblems++;
      group.problems.push({ ...problem, patientName });

      // Contar status
      const status = problem.status || "aberta";
      if (status in group.statusCounts) {
        group.statusCounts[status as keyof typeof group.statusCounts]++;
      }
    }

    return Array.from(groups.values()).sort(
      (a, b) => b.totalProblems - a.totalProblems
    );
  }, [problems, patients]);

  // Processar estatísticas de problemas
  const { problemStats, totalProblems } = useMemo(() => {
    const statusMap = new Map<string, number>();

    for (const problem of problems) {
      const status = problem.status || "aberta";
      statusMap.set(status, (statusMap.get(status) || 0) + 1);
    }

    const total = problems.length;
    const stats: ProblemStats[] = Array.from(statusMap.entries())
      .map(([status, count]) => ({
        status,
        count,
        percentage: total > 0 ? (count / total) * 100 : 0,
      }))
      .sort((a, b) => b.count - a.count);

    return {
      problemStats: stats,
      totalProblems: total,
    };
  }, [problems]);

  // Processar todos os problemas agrupados
  const allProblemsData = useMemo(() => {
    const problemMap = new Map<string, number>();
    for (const problem of problems) {
      problemMap.set(
        problem.problem,
        (problemMap.get(problem.problem) || 0) + 1
      );
    }
    return Array.from(problemMap.entries())
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value);
  }, [problems]);

  // Processar top 5 problemas para o gráfico
  const topProblemsData = useMemo(() => {
    return allProblemsData.slice(0, 5);
  }, [allProblemsData]);

  // Filtrar problemas para a tabela de pesquisa
  const filteredProblemsData = useMemo(() => {
    if (!problemSearch.trim()) return allProblemsData;
    const q = problemSearch.toLowerCase();
    return allProblemsData.filter((item) =>
      item.label.toLowerCase().includes(q)
    );
  }, [allProblemsData, problemSearch]);

  // Mapa de tradução de status
  const statusTranslation: Record<string, string> = {
    active: "Ativo",
    recurrence: "Recorrente",
    relapse: "Recaída",
    inactive: "Inativo",
    remission: "Remissão",
    resolved: "Resolvido",
    aberta: "Em Aberto",
    melhora: "Melhora",
    piora: "Piora",
    estável: "Estável",
  };

  // Dados para o gráfico de pizza (agora barra)
  const chartData = useMemo(() => {
    return problemStats.map((stat, idx) => ({
      id: idx,
      value: stat.count,
      percentage: stat.percentage,
      label:
        statusTranslation[stat.status] ||
        stat.status.charAt(0).toUpperCase() + stat.status.slice(1),
    }));
  }, [problemStats]);

  // Função para lidar com o clique na linha da tabela
  const handleRowClick = (row: any) => {
    const group = patientGroups.find((g) => g.patientName === row.patientName);
    if (group) {
      setSelectedPatient(group);
      setPatientProblems(group.problems);
      setDialogSearch(""); // Resetar busca ao abrir o diálogo
      setPatientDialogOpen(true);
    }
  };

  // Filtrar grupos de pacientes baseado na busca
  const filteredPatientGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? patientGroups.filter(
          (group) =>
            group.patientName?.toLowerCase().includes(q) ||
            group.problems.some(
              (p) =>
                p.problem?.toLowerCase().includes(q) ||
                p.status?.toLowerCase().includes(q)
            )
        )
      : patientGroups;

    return filtered.map((group) => ({
      patientName: group.patientName,
      totalProblems: group.totalProblems,
      melhora: group.statusCounts.melhora,
      aberta: group.statusCounts.aberta,
      estavel: group.statusCounts.estável,
      piora: group.statusCounts.piora,
    }));
  }, [patientGroups, search]);

  // Filtrar problemas do diálogo baseado na busca
  const filteredDialogProblems = useMemo(() => {
    const q = dialogSearch.trim().toLowerCase();
    return q
      ? patientProblems.filter(
          (problem) =>
            problem.problem?.toLowerCase().includes(q) ||
            problem.status?.toLowerCase().includes(q) ||
            problem.practitioner?.toLowerCase().includes(q)
        )
      : patientProblems;
  }, [patientProblems, dialogSearch]);

  // Renderizar skeleton durante carregamento
  if (loading) {
    return (
      <Stack spacing={3} width="100%">
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Skeleton variant="text" width={200} height={32} />
              <Skeleton variant="rectangular" height={250} />
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    );
  }

  return (
    <Stack width="100%" spacing={3}>
      {/* Barra de Filtros */}
      <ViewHeader
        title="Problemas"
        dateFilter={selectedDate}
        filterType={filterType}
        setDateFilter={handleDateChange}
        setFilterType={handleFilterTypeChangeWrapper}
        showAdminFilters={true}
        adminFiltersProps={{
          selectedLocation,
          selectedPractitioner,
          onLocationChange: handleLocationChange,
          onPractitionerChange: handlePractitionerChange,
          location,
          locationLoading,
          practitionerRole,
          practitioner,
          practitionerRoleLoading,
          onResetFilters: () => {
            handleLocationChange("");
            handlePractitionerChange("");
          },
          setIsSearchActive: () => {},
        }}
        onReset={handleReset}
        resetButtonProps={{
          tooltip: "Limpar todos os filtros",
        }}
      />

      {totalProblems > 0 ? (
        <>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs value={currentTab} onChange={handleTabChange}>
              <Tab label="Dados Gerais" />
              <Tab label="Detalhamento por Paciente" />
            </Tabs>
          </Box>

          <TabPanel value={currentTab} index={0}>
            <Card>
              <CardContent>
                <Stack spacing={5}>
                  {/* Gráfico de Status dos Problemas */}
                  {chartData.length > 0 ? (
                    <Box>
                      <Typography
                        variant="subtitle1"
                        gutterBottom
                        sx={{ fontWeight: "bold" }}
                      >
                        {(() => {
                          const sorted = [...chartData].sort(
                            (a, b) => b.value - a.value
                          );
                          const total = chartData.reduce(
                            (acc, item) => acc + item.value,
                            0
                          );
                          const percentage =
                            total > 0
                              ? Math.round((sorted[0].value / total) * 100)
                              : 0;
                          return `${percentage}% dos problemas estão como '${sorted[0].label}'`;
                        })()}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        paragraph
                        sx={{ fontSize: "0.9rem" }}
                      >
                        Avalie a eficácia dos tratamentos. Uma tendência de
                        "Melhora" indica sucesso, enquanto "Piora" pode exigir
                        uma reavaliação do plano de cuidados.
                      </Typography>
                      <Box sx={{ height: 250, width: "100%", mt: 2 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={chartData}
                            layout="vertical"
                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" allowDecimals={false} />
                            <YAxis
                              dataKey="label"
                              type="category"
                              width={calculateYAxisWidth(chartData)}
                              tick={{ fontSize: 12 }}
                            />
                            <RechartsTooltip
                              formatter={(value: number | undefined) => {
                                if (value === undefined)
                                  return ["0", "Quantidade"];
                                const total = chartData.reduce(
                                  (acc, item) => acc + item.value,
                                  0
                                );
                                const percentage =
                                  total > 0
                                    ? Math.round((value / total) * 100)
                                    : 0;
                                return [
                                  `${value} (${percentage}%)`,
                                  "Quantidade",
                                ];
                              }}
                            />
                            <Bar dataKey="value" name="Quantidade">
                              {chartData.map((entry, index) => {
                                const maxValue = Math.max(
                                  ...chartData.map((d) => d.value)
                                );
                                const isHighlight = entry.value === maxValue;
                                return (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={
                                      isHighlight
                                        ? theme.palette.primary.main
                                        : "#9E9E9E"
                                    }
                                  />
                                );
                              })}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>
                  ) : (
                    <NoData message="Sem dados de status de problemas." />
                  )}

                  {/* Gráfico de Top 5 Problemas */}
                  {topProblemsData.length > 0 && <Divider />}

                  {topProblemsData.length > 0 ? (
                    <Box>
                      <Typography
                        variant="subtitle1"
                        gutterBottom
                        sx={{ fontWeight: "bold" }}
                      >
                        {(() => {
                          const total = allProblemsData.reduce(
                            (acc, item) => acc + item.value,
                            0
                          );
                          const percentage =
                            total > 0
                              ? Math.round(
                                  (topProblemsData[0].value / total) * 100
                                )
                              : 0;
                          return `'${topProblemsData[0].label}' representa ${percentage}% dos problemas`;
                        })()}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        paragraph
                        sx={{ fontSize: "0.9rem" }}
                      >
                        Veja os 5 problemas de saúde mais registrados. Como são
                        textos livres, use a tabela abaixo para pesquisar todos
                        os problemas cadastrados.
                      </Typography>
                      <Box sx={{ height: 250, width: "100%", mt: 2 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={topProblemsData}
                            layout="vertical"
                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" allowDecimals={false} />
                            <YAxis
                              dataKey="label"
                              type="category"
                              width={calculateYAxisWidth(topProblemsData)}
                              tick={{ fontSize: 12 }}
                            />
                            <RechartsTooltip
                              formatter={(value: number | undefined) => {
                                if (value === undefined)
                                  return ["0", "Quantidade"];
                                const total = allProblemsData.reduce(
                                  (acc, item) => acc + item.value,
                                  0
                                );
                                const percentage =
                                  total > 0
                                    ? Math.round((value / total) * 100)
                                    : 0;
                                return [
                                  `${value} (${percentage}%)`,
                                  "Quantidade",
                                ];
                              }}
                            />
                            <Bar dataKey="value" name="Quantidade">
                              {topProblemsData.map((entry, index) => {
                                const maxValue = Math.max(
                                  ...topProblemsData.map((d) => d.value)
                                );
                                const isHighlight = entry.value === maxValue;
                                return (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={
                                      isHighlight
                                        ? theme.palette.primary.main
                                        : "#9E9E9E"
                                    }
                                  />
                                );
                              })}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>

                      {/* Tabela de todos os problemas */}
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Todos os Problemas ({allProblemsData.length} tipos
                          diferentes)
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                          <TextField
                            size="small"
                            variant="outlined"
                            fullWidth
                            placeholder="Buscar problema..."
                            value={problemSearch}
                            onChange={(e) => setProblemSearch(e.target.value)}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon fontSize="small" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Box>
                        <GenericTable
                          tableColumns={[
                            {
                              column: "Problema",
                              rowName: "label",
                              align: "left",
                            },
                            {
                              column: "Quantidade",
                              rowName: "value",
                              width: "120px",
                            },
                            {
                              column: "Percentual",
                              rowName: "percentage",
                              width: "120px",
                            },
                          ]}
                          tableRows={filteredProblemsData.map((item) => {
                            const total = allProblemsData.reduce(
                              (acc, i) => acc + i.value,
                              0
                            );
                            const percentage =
                              total > 0
                                ? Math.round((item.value / total) * 100)
                                : 0;
                            return {
                              ...item,
                              percentage: `${percentage}%`,
                            };
                          })}
                          characterLimit={80}
                        />
                      </Box>
                    </Box>
                  ) : (
                    <NoData message="Sem dados de problemas registrados." />
                  )}
                </Stack>
              </CardContent>
            </Card>
          </TabPanel>

          <TabPanel value={currentTab} index={1}>
            <Card>
              <CardContent>
                <Box sx={{ mb: 2 }}>
                  <TextField
                    size="small"
                    variant="outlined"
                    fullWidth
                    placeholder="Buscar por paciente ou problema"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                <GenericTable
                  tableColumns={[
                    {
                      column: "Paciente",
                      rowName: "patientName",
                      align: "left",
                    },
                    { column: "Melhora", rowName: "melhora", width: "100px" },
                    { column: "Aberta", rowName: "aberta", width: "80px" },
                    { column: "Estável", rowName: "estavel", width: "80px" },
                    { column: "Piora", rowName: "piora", width: "80px" },
                    {
                      column: "Total",
                      rowName: "totalProblems",
                      width: "80px",
                    },
                  ]}
                  tableRows={filteredPatientGroups}
                  characterLimit={60}
                  onRowClick={handleRowClick}
                />
              </CardContent>
            </Card>
          </TabPanel>
        </>
      ) : (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            Nenhum problema encontrado
          </Typography>
        </Box>
      )}

      {/* Dialog para exibir problemas do paciente */}
      {patientDialogOpen && selectedPatient && (
        <Dialog
          open={patientDialogOpen}
          onClose={() => setPatientDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>Problemas de: {selectedPatient.patientName}</DialogTitle>
          <DialogContent dividers>
            <Box sx={{ mb: 2 }}>
              <TextField
                size="small"
                variant="outlined"
                fullWidth
                placeholder="Buscar por problema, status ou profissional"
                value={dialogSearch}
                onChange={(e) => setDialogSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {filteredDialogProblems.length > 0 ? (
              <GenericTable
                tableColumns={[
                  { column: "Problema", rowName: "problem", align: "left" },
                  { column: "Status", rowName: "statusLabel", width: "150px" },
                  { column: "Profissional", rowName: "practitioner" },
                  { column: "Data", rowName: "date", width: "120px" },
                ]}
                tableRows={filteredDialogProblems.map((problem) => ({
                  problem: problem.problem,
                  statusLabel: problem.status
                    ? problem.status.charAt(0).toUpperCase() +
                      problem.status.slice(1)
                    : "Não informado",
                  practitioner: problem.practitioner,
                  date: problem.date,
                }))}
                characterLimit={80}
              />
            ) : (
              <Typography variant="body1" align="center" sx={{ py: 2 }}>
                {dialogSearch
                  ? "Nenhum problema encontrado com os critérios de busca."
                  : "Nenhum problema encontrado para este paciente."}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setPatientDialogOpen(false);
                setSelectedPatient(null);
                setPatientProblems([]);
                setDialogSearch("");
              }}
            >
              Fechar
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Stack>
  );
}
